import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLogPatientVerificationMutation,
  useLogVerificationMutation,
} from '@features/patient-coverages';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useEligibilityBadgeState } from '../hooks/useEligibilityBadgeState';
import { ELIG_TOKENS } from '../theme/readinessTokens';

import type { EligibilitySource } from './EligibilitySummaryContent';

interface Props {
  patientId?: string;
  encounterId?: string;
  source: EligibilitySource;
  onClose: () => void;
}

type Method = 'phone' | 'portal';

interface FinancialFieldDef {
  field:
    | 'copayment'
    | 'coinsurance'
    | 'deductible'
    | 'remaining_deductible'
    | 'oop_max'
    | 'remaining_oop_max'
    | 'member_obligation';
  label: string;
  prefix: '$' | '%';
}

const FINANCIAL_FIELDS: FinancialFieldDef[] = [
  { field: 'copayment', label: 'Copay', prefix: '$' },
  { field: 'coinsurance', label: 'Coinsurance', prefix: '%' },
  { field: 'deductible', label: 'Deductible', prefix: '$' },
  { field: 'remaining_deductible', label: 'Remaining deductible', prefix: '$' },
  { field: 'oop_max', label: 'Out-of-pocket max', prefix: '$' },
  { field: 'remaining_oop_max', label: 'Remaining OOP max', prefix: '$' },
  { field: 'member_obligation', label: 'Member obligation', prefix: '$' },
];

function nowDatetimeLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

function dollarsToCents(value: string): number | null {
  if (!value) return null;
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function centsToDollars(cents: unknown): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
  return (cents / 100).toFixed(2);
}

/**
 * Inline verification form. Replaces the legacy "manual override" surface
 * with a structured event log: how the user verified (phone or portal),
 * when, who they spoke with, what they confirmed, and any financial
 * adjustments. Submission creates a manual `eligibility_snapshots` row
 * via POST /v2/provider/encounters/{id}/eligibility/verifications.
 */
export const VerificationForm: React.FC<Props> = ({
  patientId,
  encounterId,
  source,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { eligibilityChecks } = useEventTracking();
  const { eligibilityData } = useEligibilityBadgeState({ patientId, encounterId });
  const [logVerification, { isLoading: isLoadingEncounter }] =
    useLogVerificationMutation();
  const [logPatientVerification, { isLoading: isLoadingPatient }] =
    useLogPatientVerificationMutation();
  const isLoading = isLoadingEncounter || isLoadingPatient;

  // Prefer the encounter-scoped endpoint when we have an encounter (caller
  // passed one or the BE pinned one) — keeps the verification anchored to
  // a specific visit. Fall back to the patient-scoped endpoint when no
  // encounter is available so users can log verifications during slow
  // periods between visits.
  const resolvedEncounterId =
    encounterId ?? eligibilityData?.pinned_encounter_id ?? undefined;

  const [method, setMethod] = useState<Method>('phone');
  const [contactedAt, setContactedAt] = useState<string>(nowDatetimeLocal());
  const [repName, setRepName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [showMoreFinancials, setShowMoreFinancials] = useState(false);
  const [financials, setFinancials] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const initialFinancials = useMemo<Record<string, string>>(() => {
    const fin = eligibilityData?.financials ?? {};
    const initial: Record<string, string> = {};
    for (const { field, prefix } of FINANCIAL_FIELDS) {
      if (prefix === '$') {
        initial[field] = centsToDollars(fin[`${field}_cents`]);
      } else {
        const raw: unknown = fin[field];
        initial[field] = typeof raw === 'number' ? String(raw) : '';
      }
    }
    return initial;
  }, [eligibilityData]);

  // Seed the form with the snapshot's current financials so the user can
  // see what's there and only edit deltas. Re-runs when the snapshot itself
  // changes (a fresh BE response with different values).
  useEffect(() => {
    setFinancials(initialFinancials);
  }, [initialFinancials]);

  const hasDirtyFields = useMemo(
    () =>
      repName.trim().length > 0 ||
      referenceNumber.trim().length > 0 ||
      notes.trim().length > 0,
    [repName, referenceNumber, notes],
  );

  const fieldsAdjustedCount = useMemo(() => {
    let count = 0;
    for (const { field } of FINANCIAL_FIELDS) {
      if ((financials[field] ?? '') !== (initialFinancials[field] ?? '')) {
        count += 1;
      }
    }
    return count;
  }, [financials, initialFinancials]);

  const handleClose = useCallback(() => {
    eligibilityChecks.verificationFormClosed?.({
      patient_id: patientId,
      encounter_id: encounterId,
      source,
      had_dirty_fields: hasDirtyFields,
    });
    onClose();
  }, [eligibilityChecks, patientId, encounterId, source, hasDirtyFields, onClose]);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!patientId) {
      setError('No patient context for this verification.');
      return;
    }
    if (!notes.trim()) {
      setError('Please describe what was verified.');
      return;
    }

    const isPhone = method === 'phone';

    // Convert the per-field input strings into a financials patch shaped the
    // way the BE expects: `{field}_cents` for currency, raw number for
    // percent. Empty strings are dropped — they mean "no change."
    const financialsPatch: Record<string, number> = {};
    for (const { field, prefix } of FINANCIAL_FIELDS) {
      const raw = financials[field] ?? '';
      if (raw === '') continue;
      if (prefix === '$') {
        const cents = dollarsToCents(raw);
        if (cents !== null) financialsPatch[`${field}_cents`] = cents;
      } else {
        const n = Number.parseFloat(raw);
        if (Number.isFinite(n)) financialsPatch[field] = n;
      }
    }

    const manualUpdateRequest = {
      method,
      notes: notes.trim(),
      verification_contacted_at: new Date(contactedAt).toISOString(),
      verification_rep_name:
        isPhone && repName.trim() ? repName.trim() : null,
      verification_reference_number:
        isPhone && referenceNumber.trim() ? referenceNumber.trim() : null,
      financials:
        Object.keys(financialsPatch).length > 0 ? financialsPatch : null,
    };

    try {
      // Prefer the encounter-scoped path when an encounter is available — it
      // anchors the verification to a specific visit. Fall back to the
      // patient-scoped path for between-visits logging.
      if (resolvedEncounterId) {
        await logVerification({
          encounterId: resolvedEncounterId,
          manualUpdateRequest,
        }).unwrap();
      } else {
        await logPatientVerification({
          patientId,
          manualUpdateRequest,
        }).unwrap();
      }

      eligibilityChecks.verificationSubmitted?.({
        patient_id: patientId,
        encounter_id: resolvedEncounterId,
        source,
        method,
        has_rep_name: Boolean(isPhone && repName.trim()),
        has_reference_number: Boolean(isPhone && referenceNumber.trim()),
        fields_adjusted_count: fieldsAdjustedCount,
      });
      enqueueSnackbar('Verification logged', { variant: 'success' });
      onClose();
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { detail?: string } }).data?.detail
          : null;
      setError(detail ?? 'Failed to log verification.');
    }
  }, [
    contactedAt,
    eligibilityChecks,
    enqueueSnackbar,
    fieldsAdjustedCount,
    financials,
    logPatientVerification,
    logVerification,
    method,
    notes,
    onClose,
    patientId,
    referenceNumber,
    repName,
    resolvedEncounterId,
    source,
  ]);

  const isPhone = method === 'phone';
  const submitDisabled = isLoading || !notes.trim() || !patientId;

  return (
    <Box
      sx={{
        border: `1px solid ${ELIG_TOKENS.line}`,
        borderRadius: 1.5,
        p: 2.5,
        my: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: ELIG_TOKENS.inkFaint,
          fontWeight: 600,
          letterSpacing: '0.06em',
          fontSize: 10,
          lineHeight: 1,
          display: 'block',
          mb: 1.5,
        }}
      >
        Log a verification
      </Typography>

      <Stack spacing={2}>
        <FormControl>
          <Typography
            variant="body2"
            sx={{ color: ELIG_TOKENS.ink, fontWeight: 500, mb: 0.5 }}
          >
            How did you verify?
          </Typography>
          <RadioGroup
            row
            value={method}
            onChange={(e) => setMethod(e.target.value as Method)}
          >
            <FormControlLabel
              value="phone"
              control={<Radio size="small" />}
              label="Phone"
            />
            <FormControlLabel
              value="portal"
              control={<Radio size="small" />}
              label="Portal"
            />
          </RadioGroup>
        </FormControl>

        <TextField
          label="When?"
          type="datetime-local"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={contactedAt}
          onChange={(e) => setContactedAt(e.target.value)}
        />

        <Collapse in={isPhone} unmountOnExit>
          <Stack spacing={2}>
            <TextField
              label="Who you spoke with"
              size="small"
              fullWidth
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
              placeholder="e.g. Janelle"
            />
            <TextField
              label="Reference number"
              size="small"
              fullWidth
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              placeholder="e.g. ABC123"
            />
          </Stack>
        </Collapse>

        <TextField
          label="What was verified"
          size="small"
          fullWidth
          multiline
          required
          minRows={3}
          maxRows={8}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Coverage active until 12/31, BH covered, $50 copay…"
        />

        <Box>
          <Typography
            variant="body2"
            sx={{ color: ELIG_TOKENS.inkSoft, fontWeight: 500, mb: 1 }}
          >
            Adjustments to current data (optional)
          </Typography>
          <TextField
            label="Copay"
            size="small"
            fullWidth
            type="number"
            value={financials.copayment ?? ''}
            onChange={(e) =>
              setFinancials((prev) => ({ ...prev, copayment: e.target.value }))
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />

          <Box
            component="button"
            type="button"
            onClick={() => setShowMoreFinancials((v) => !v)}
            sx={{
              background: 'none',
              border: 'none',
              p: 0,
              mt: 1,
              cursor: 'pointer',
              color: ELIG_TOKENS.inkSoft,
              fontSize: 13,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {showMoreFinancials ? '− Fewer fields' : '+ More fields'}
          </Box>

          <Collapse in={showMoreFinancials} unmountOnExit>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {FINANCIAL_FIELDS.filter((f) => f.field !== 'copayment').map(
                ({ field, label, prefix }) => (
                  <TextField
                    key={field}
                    label={label}
                    size="small"
                    fullWidth
                    type="number"
                    value={financials[field] ?? ''}
                    onChange={(e) =>
                      setFinancials((prev) => ({
                        ...prev,
                        [field]: e.target.value,
                      }))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{prefix}</InputAdornment>
                      ),
                    }}
                  />
                ),
              )}
            </Stack>
          </Collapse>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="text"
            size="small"
            onClick={handleClose}
            disabled={isLoading}
            sx={{ textTransform: 'none', color: ELIG_TOKENS.inkSoft }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitDisabled}
            startIcon={
              isLoading ? <CircularProgress size={14} color="inherit" /> : undefined
            }
            sx={{
              bgcolor: ELIG_TOKENS.midnight,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: ELIG_TOKENS.midnight,
                filter: 'brightness(0.92)',
              },
            }}
          >
            Submit verification
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
